import { ResultResponse } from "@/types/common";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef } from "react";

interface InfiniteScrollPaginationProps<T> {
    paginated_response: ResultResponse<T>;
    onLoadMore: () => void;
    isLoading?: boolean;
    hasMore: boolean;
    scrollContainerRef?: React.RefObject<HTMLElement>;
    currentPage?: number;
    pageSize?: number;
    isDataRefreshing?: boolean;
}

export function ObjectListPagination2<T>({
    paginated_response,
    onLoadMore,
    isLoading = false,
    hasMore,
    scrollContainerRef,
    // currentPage = 1,
    // pageSize = 25,
    isDataRefreshing = false
}: InfiniteScrollPaginationProps<T>) {
    const loadMoreRef = useRef<HTMLDivElement>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const isLoadingRef = useRef(false);
    const lastTriggerTime = useRef(0);

    console.log("hasMore", hasMore);

    // Mettre à jour la référence de chargement
    useEffect(() => {
        isLoadingRef.current = isLoading;
    }, [isLoading]);

    // Fonction de chargement avec debounce
    const debouncedLoadMore = useCallback(() => {
        const now = Date.now();
        // Empêcher les appels trop rapprochés (moins de 1 seconde)
        if (now - lastTriggerTime.current < 1000) {
            return;
        }

        if (!isLoadingRef.current && hasMore) {
            lastTriggerTime.current = now;
            onLoadMore();
        }
    }, [onLoadMore, hasMore]);

    // Utiliser Intersection Observer pour détecter quand on arrive en bas
    useEffect(() => {
        // Nettoyer l'observer précédent
        if (observerRef.current) {
            observerRef.current.disconnect();
        }

        // Ne pas créer d'observer s'il n'y a plus de données
        if (!hasMore) {
            console.log("No more data");
            return;
        }

        observerRef.current = new IntersectionObserver(
            entries => {
                const target = entries[0];
                if (target.isIntersecting) {
                    debouncedLoadMore();
                }
            },
            {
                threshold: 0.1,
                rootMargin: "10px",
                root: scrollContainerRef?.current || null // Utiliser le conteneur scrollable comme root
            }
        );

        const currentRef = loadMoreRef.current;
        if (currentRef && observerRef.current) {
            observerRef.current.observe(currentRef);
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [hasMore, debouncedLoadMore, scrollContainerRef]);

    // Désactiver l'observer pendant le chargement
    useEffect(() => {
        if (isLoading && observerRef.current && loadMoreRef.current) {
            observerRef.current.unobserve(loadMoreRef.current);
        } else if (!isLoading && observerRef.current && loadMoreRef.current && hasMore) {
            observerRef.current.observe(loadMoreRef.current);
        }
    }, [isLoading, hasMore]);

    // Détection du scroll en bas de page/container pour déclencher le chargement
    useEffect(() => {
        const handleScroll = () => {
            console.log("handleScroll");
            if (isLoading || !hasMore) return;
            let container: HTMLElement | Window = window;
            if (scrollContainerRef && scrollContainerRef.current) {
                container = scrollContainerRef.current;
            }
            let scrollBottom = 0;
            let scrollHeight = 0;
            // let clientHeight = 0;
            if (container instanceof Window) {
                scrollBottom = window.scrollY + window.innerHeight;
                scrollHeight = document.documentElement.scrollHeight;
                // clientHeight = window.innerHeight;
            } else {
                scrollBottom = container.scrollTop + container.clientHeight;
                scrollHeight = container.scrollHeight;
                // clientHeight = container.clientHeight;
            }            
            // Déclencher à 100px du bas
            if (scrollHeight - scrollBottom < 100) {
                onLoadMore();
            }
        };
        const target = scrollContainerRef && scrollContainerRef.current ? scrollContainerRef.current : window;
        target.addEventListener("scroll", handleScroll);
        return () => {
            target.removeEventListener("scroll", handleScroll);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, hasMore, onLoadMore, scrollContainerRef]);

    // Correction robuste : boucle de vérification après chaque chargement
    useEffect(() => {
        let cancelled = false;
        async function checkAndLoadMore() {
            // On boucle tant que le trigger est visible et qu'il y a des données à charger
            while (!cancelled && !isLoading && hasMore && loadMoreRef.current) {
                // Attendre un petit délai pour laisser le DOM se mettre à jour
                await new Promise(res => setTimeout(res, 30));
                const trigger = loadMoreRef.current;
                if (!trigger) break;
                let container: HTMLElement | Window = window;
                if (scrollContainerRef && scrollContainerRef.current) {
                    container = scrollContainerRef.current;
                }
                const triggerRect = trigger.getBoundingClientRect();
                let containerRect;
                if (container instanceof Window) {
                    containerRect = {
                        top: 0,
                        bottom: window.innerHeight
                    };
                } else {
                    containerRect = container.getBoundingClientRect();
                }
                // Si le trigger est visible dans le container
                if (triggerRect.bottom > containerRect.top && triggerRect.top < containerRect.bottom) {
                    debouncedLoadMore();
                    // Attendre que le chargement commence
                    await new Promise(res => setTimeout(res, 100));
                    break; // On attend la prochaine fin de chargement pour relancer la boucle
                } else {
                    break;
                }
            }
        }
        checkAndLoadMore();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, hasMore, debouncedLoadMore, scrollContainerRef]);

    // Afficher les informations de chargement seulement s'il y a plus de données
    if (!hasMore && paginated_response.results.length === 0) {
        return null;
    }

    return (
        <div className="flex w-full flex-col items-center justify-center">
            {/* Élément de déclenchement du scroll infini */}
            <div ref={loadMoreRef} className=" w-full">
                {(isLoading && hasMore) || isDataRefreshing ? (
                    <div className="flex h-12 items-center justify-center space-x-2 border-b border-gray-100">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm text-gray-500">{isDataRefreshing ? "Refreshing..." : "Loading..."}</span>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
